import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import { API_URL } from '@/constants/config';
import { tokenStorage } from '@/utils/tokenStorage';
import i18n from '@/locales/i18n';

// Authenticated API client for the tenant web portal.
//
// Mirrors `source/mobile/src/api/client.ts` so contract-wise mobile and web are
// interchangeable:
//   - baseURL: VITE_API_URL  (includes /api prefix)
//   - Bearer token from localStorage (see PLAN_WEB.md §Безопасность токена)
//   - Accept-Language sent on every request
//   - 401 → clear token + redirect via registered handler

type OnUnauthorizedHandler = () => void;

let onUnauthorized: OnUnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: OnUnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15_000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['Accept-Language'] = i18n.language ?? 'ru';
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      tokenStorage.clear();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export function extractApiError(err: unknown): { message: string; errors?: Record<string, string[]> } {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    return {
      message: data?.message ?? err.message ?? 'Unknown error',
      errors: data?.errors,
    };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}
